import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Building, PlusCircle, Users, Bot } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import useOrganizationStore from '@/stores/useOrganizationStore'

const OrganizationsPage = () => {
  const { organizations, fetchOrganizations, isLoading } =
    useOrganizationStore()

  useEffect(() => {
    fetchOrganizations()
  }, [fetchOrganizations])

  return (
    <div className="container max-w-6xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Organizations</h1>
        <Button>
          <PlusCircle className="mr-2 h-4 w-4" /> Create Organization
        </Button>
      </div>
      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-1/4" />
                <div className="flex justify-between mt-4">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((org) => (
            <Link to={`/organizations/${org.id}`} key={org.id}>
              <Card className="hover:border-primary hover:bg-accent transition-colors h-full flex flex-col">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-muted rounded-md">
                        <Building className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle>{org.name}</CardTitle>
                        <CardDescription>
                          {org.isDefault && (
                            <Badge variant="secondary" className="mr-2">
                              Default
                            </Badge>
                          )}
                          ID: {org.id}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge
                      variant={org.status === 'active' ? 'default' : 'outline'}
                    >
                      {org.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow flex flex-col justify-end">
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{org.members.length} Members</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Bot className="h-4 w-4" />
                      <span>{org.remoteAgents.length} Agents</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default OrganizationsPage
